import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, Copy, Loader2, TrendingUp, DollarSign, Calendar, Building2, Edit, FileText, AlertCircle } from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useDeal } from '../contexts/DealContext';
import { DatabaseService } from '../lib/database';
import { ScenarioCreateModal } from './ScenarioCreateModal';

interface ScenarioOption {
  id: string;
  name: string;
}

interface ScenarioData {
  id: string;
  name: string;
  headline_price: number;
  business_name: string;
  bank_amount: number;
  bank_rate_annual: number;
  bank_term_years: number;
  seller_amount: number;
  seller_rate_annual: number;
  seller_structure: string;
  seller_amortization_years?: number;
  seller_balloon_years: number;
  seller_balloon_due_month: number;
  seller_io_months?: number;
  annual_sde: number;
  monthly_sde: number;
  financial_metric_type: string;
  annual_threshold: number;
  quarterly_threshold: number;
}

export function ScenarioAnalysisPage() {
  const { user } = useUser();
  const navigate = useNavigate();
  const { dealId, scenarioId } = useParams<{ dealId: string; scenarioId?: string }>();
  const { selectedDeal, selectedScenarioId, selectScenario } = useDeal();

  const [scenarios, setScenarios] = useState<ScenarioOption[]>([]);
  const [currentScenario, setCurrentScenario] = useState<ScenarioData | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showCloneModal, setShowCloneModal] = useState(false);

  // Load scenarios for selected deal
  useEffect(() => {
    async function load() {
      if (!dealId || !user?.id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await DatabaseService.listDealScenarios(dealId, user.id);
        if (res.error) {
          setError(res.error);
          setScenarios([]);
        } else {
          const opts = (res.data || []).map((s: any) => ({ id: s.id, name: s.name }));
          setScenarios(opts);
          // Ensure a scenario is selected (URL param, context, or first)
          const preferred = scenarioId || selectedScenarioId || opts[0]?.id || null;
          if (preferred) {
            if (preferred !== selectedScenarioId) selectScenario(preferred);
          }
        }
      } finally {
        setLoading(false);
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId, user?.id]);

  // Load current scenario details
  useEffect(() => {
    async function loadScenarioDetails() {
      if (!selectedScenarioId || !user?.id) return;
      setLoading(true);
      try {
        // For now, we'll use the scenario data from the list
        // In the future, we could add a specific function to get scenario details
        const res = await DatabaseService.listDealScenarios(dealId!, user.id);
        if (res.data) {
          const scenario = res.data.find((s: any) => s.id === selectedScenarioId);
          if (scenario) {
            setCurrentScenario(scenario);
          }
        }
      } finally {
        setLoading(false);
      }
    }
    loadScenarioDetails();
  }, [selectedScenarioId, dealId, user?.id]);

  // Keep URL in sync with selection
  useEffect(() => {
    if (!dealId) return;
    if (!selectedScenarioId && scenarios.length > 0) return;
    if (selectedScenarioId && scenarioId !== selectedScenarioId) {
      navigate(`/deals/${dealId}/scenarios/${selectedScenarioId}/analysis`, { replace: true });
    }
  }, [dealId, scenarioId, selectedScenarioId, scenarios.length, navigate]);

  const hasScenarios = scenarios.length > 0;



  // Calculate debt service and DSCR
  const calculateDebtService = () => {
    if (!currentScenario || !currentScenario.metadata) return null;

    const bankAmount = currentScenario.metadata.bankAmount || 0;
    const bankRate = currentScenario.metadata.bankRateAnnual || 0;
    const bankTermYears = currentScenario.metadata.bankTermYears || 0;
    const sellerAmount = currentScenario.metadata.sellerAmount || 0;
    const sellerRate = currentScenario.metadata.sellerRateAnnual || 0;
    const sellerBalloonYears = currentScenario.metadata.sellerBalloonYears || 0;
    const annualSde = currentScenario.metadata.annualSde || 0;

    // Calculate monthly bank payment (amortizing)
    const monthlyBankRate = bankRate / 12;
    const bankPayments = bankTermYears * 12;
    const monthlyBankPayment = bankAmount > 0 && bankPayments > 0 
      ? (bankAmount * monthlyBankRate * Math.pow(1 + monthlyBankRate, bankPayments)) / 
        (Math.pow(1 + monthlyBankRate, bankPayments) - 1)
      : 0;

    // Calculate monthly seller payment
    let monthlySellerPayment = 0;
    if (sellerAmount > 0) {
      if (currentScenario.seller_structure === 'amort') {
        // Amortizing payment
        const sellerAmortYears = currentScenario.seller_amortization_years || sellerBalloonYears;
        const monthlySellerRate = sellerRate / 12;
        const sellerPayments = sellerAmortYears * 12;
        monthlySellerPayment = (sellerAmount * monthlySellerRate * Math.pow(1 + monthlySellerRate, sellerPayments)) / 
          (Math.pow(1 + monthlySellerRate, sellerPayments) - 1);
      } else if (currentScenario.seller_structure === 'io') {
        // Interest only
        monthlySellerPayment = sellerAmount * (sellerRate / 12);
      } else if (currentScenario.seller_structure === 'io_then_amort') {
        // IO then amortizing - for simplicity, show IO payment
        monthlySellerPayment = sellerAmount * (sellerRate / 12);
      }
    }

    const totalMonthlyDebtService = monthlyBankPayment + monthlySellerPayment;
    const annualDebtService = totalMonthlyDebtService * 12;
    const dscr = annualSde > 0 ? annualSde / annualDebtService : 0;

    return {
      monthlyBankPayment,
      monthlySellerPayment,
      totalMonthlyDebtService,
      annualDebtService,
      dscr
    };
  };

  const debtService = calculateDebtService();

  if (!dealId) {
    return <div>No deal selected</div>;
  }

  if (!selectedDeal) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading deal...</p>
        </div>
      </div>
    );
  }

  // Check if user has read-only access - show limited scenario analysis access
  const isReadOnlyAccess = selectedDeal.access_type === 'read';

  return (
    <div className="py-4 px-4 sm:py-6 sm:px-6 lg:py-8 lg:px-8">
      {/* Deal Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">{selectedDeal.business_name}</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="inline-flex items-center px-3 py-1.5 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700"
              onClick={() => navigate(`/deals/${dealId}/dashboard`)}
            >
              <Building2 className="w-4 h-4 mr-1" />
              Back to Dashboard
            </button>
          </div>
        </div>
        {selectedDeal.description && (
          <p className="text-gray-600 mb-4">{selectedDeal.description}</p>
        )}
        
        {/* Read-only access notice */}
        {isReadOnlyAccess && (
          <div className="mt-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              <div>
                <h4 className="text-sm font-medium text-blue-900">Read-Only Access</h4>
                <p className="text-sm text-blue-800">
                  You have read-only access to scenario analysis. You can view all data but cannot make changes.
                </p>
              </div>
            </div>
          </div>
        )}
        {selectedDeal.deal_value && (
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="font-medium">Deal Value: {new Intl.NumberFormat('en-US', { style: 'currency', currency: selectedDeal.currency, minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(selectedDeal.deal_value)}</span>
            <span>•</span>
            <span>Status: {selectedDeal.status}</span>
          </div>
        )}
      </div>

      {/* Scenario Navigation */}
      <div className="mb-8">
        <div className="flex items-center gap-2 mb-4">
          {loading ? (
            <span className="inline-flex items-center text-gray-600 text-sm"><Loader2 className="w-4 h-4 mr-1 animate-spin" /> Loading scenarios...</span>
          ) : hasScenarios ? (
            <>
              <select
                className="px-2 py-1 border rounded-md bg-white"
                value={selectedScenarioId ?? ''}
                onChange={(e) => selectScenario(e.target.value || null)}
              >
                {scenarios.map(s => (
                  <option key={s.id} value={s.id}>{s.name}</option>
                ))}
              </select>
              <button
                type="button"
                className="inline-flex items-center px-2 py-1 text-sm rounded-md bg-green-600 text-white hover:bg-green-700"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="w-4 h-4 mr-1" /> Create New Scenario
              </button>
              <button
                type="button"
                className="inline-flex items-center px-2 py-1 text-sm rounded-md bg-yellow-600 text-white hover:bg-yellow-700"
                onClick={() => setShowEditModal(true)}
                disabled={!selectedScenarioId}
              >
                <Edit className="w-4 h-4 mr-1" /> Edit
              </button>
              <button
                type="button"
                className="inline-flex items-center px-2 py-1 text-sm rounded-md bg-gray-100 text-gray-800 hover:bg-gray-200"
                onClick={() => setShowCloneModal(true)}
                disabled={!selectedScenarioId}
              >
                <Copy className="w-4 h-4 mr-1" /> Clone
              </button>
              <button
                type="button"
                className="inline-flex items-center px-2 py-1 text-sm rounded-md bg-purple-600 text-white hover:bg-purple-700"
                onClick={() => navigate(`/deals/${dealId}/scenarios/${selectedScenarioId}/proforma`)}
                disabled={!selectedScenarioId}
              >
                <FileText className="w-4 h-4 mr-1" /> Proforma
              </button>
            </>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                className="inline-flex items-center px-3 py-1.5 text-sm rounded-md bg-green-600 text-white hover:bg-green-700"
                onClick={() => setShowCreateModal(true)}
              >
                <Plus className="w-4 h-4 mr-1" /> Create New Scenario
              </button>
            </div>
          )}
          {error && <span className="text-red-600 text-sm ml-2">{error}</span>}
        </div>
      </div>

      {/* Scenario Analysis Content */}
      {currentScenario ? (
        <div className="space-y-8">
          {/* Deal Summary */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-blue-600" />
              Deal Summary - {currentScenario.name}
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              <div className="p-4 bg-blue-50 rounded-lg">
                <h3 className="font-semibold text-blue-900 mb-2">Purchase Price</h3>
                <p className="text-2xl font-bold text-blue-800">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(currentScenario.metadata?.headlinePrice || 0)}
                </p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg">
                <h3 className="font-semibold text-green-900 mb-2">Annual SDE</h3>
                <p className="text-2xl font-bold text-green-800">
                  {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(currentScenario.metadata?.annualSde || 0)}
                </p>
              </div>
              <div className="p-4 bg-purple-50 rounded-lg">
                <h3 className="font-semibold text-purple-900 mb-2">Business Name</h3>
                <p className="text-lg font-medium text-purple-800">{currentScenario.metadata?.businessName || 'N/A'}</p>
              </div>
            </div>
          </div>

          {/* Debt Service Analysis */}
          {debtService && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
              <h2 className="text-xl font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-green-600" />
                Debt Service Coverage Analysis
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h3 className="font-semibold text-blue-900 mb-2">Bank Payment (Monthly)</h3>
                  <p className="text-xl font-bold text-blue-800">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(debtService.monthlyBankPayment)}
                  </p>
                </div>
                <div className="p-4 bg-orange-50 rounded-lg">
                  <h3 className="font-semibold text-orange-900 mb-2">Seller Payment (Monthly)</h3>
                  <p className="text-xl font-bold text-orange-800">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(debtService.monthlySellerPayment)}
                  </p>
                </div>
                <div className="p-4 bg-red-50 rounded-lg">
                  <h3 className="font-semibold text-red-900 mb-2">Total Debt Service (Annual)</h3>
                  <p className="text-xl font-bold text-red-800">
                    {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(debtService.annualDebtService)}
                  </p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg">
                  <h3 className="font-semibold text-green-900 mb-2">DSCR</h3>
                  <p className="text-xl font-bold text-green-800">
                    {debtService.dscr.toFixed(2)}x
                  </p>
                </div>
              </div>

              {/* Annual Debt Service Table */}
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Year</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Bank Payment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Seller Payment</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total Debt Service</th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">DSCR</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {Array.from({ length: Math.max(currentScenario.metadata?.bankTermYears || 0, currentScenario.metadata?.sellerBalloonYears || 0) }, (_, i) => {
                      const year = i + 1;
                      const isBankActive = year <= (currentScenario.metadata?.bankTermYears || 0);
                      const isSellerActive = year <= (currentScenario.metadata?.sellerBalloonYears || 0);
                      
                      const bankPayment = isBankActive ? debtService.monthlyBankPayment * 12 : 0;
                      const sellerPayment = isSellerActive ? debtService.monthlySellerPayment * 12 : 0;
                      const totalDebtService = bankPayment + sellerPayment;
                      const dscr = (currentScenario.metadata?.annualSde || 0) > 0 ? (currentScenario.metadata?.annualSde || 0) / totalDebtService : 0;
                      
                      return (
                        <tr key={year}>
                          <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{year}</td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {isBankActive ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(bankPayment) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {isSellerActive ? new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(sellerPayment) : '-'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(totalDebtService)}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              dscr >= 1.25 ? 'bg-green-100 text-green-800' :
                              dscr >= 1.0 ? 'bg-yellow-100 text-yellow-800' :
                              'bg-red-100 text-red-800'
                            }`}>
                              {dscr.toFixed(2)}x
                            </span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="text-center py-16">
          <div className="text-gray-400 text-6xl mb-4">📊</div>
          <h2 className="text-2xl font-bold text-gray-900 mb-4">No Scenario Selected</h2>
          <p className="text-gray-600 mb-8 max-w-md mx-auto">
            Select a scenario from the dropdown above to view the analysis, or create a new scenario to get started.
          </p>
        </div>
      )}

      {/* Scenario Create Modal */}
      <ScenarioCreateModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        existingScenarios={scenarios}
        onCreate={async (scenarioData) => {
          if (!dealId || !user?.id) return;
          setLoading(true);
          try {
            const res = scenarioData.cloneFromId
              ? await DatabaseService.cloneDealScenario(scenarioData.cloneFromId, scenarioData.name, user.id)
              : await DatabaseService.createDealScenario(dealId, scenarioData, user.id);
            if (res.error) {
              setError(res.error);
              return;
            }
            const list = await DatabaseService.listDealScenarios(dealId, user.id);
            const opts = (list.data || []).map((s: any) => ({ id: s.id, name: s.name }));
            setScenarios(opts);
            const newId = res.data?.id || opts[0]?.id || null;
            if (newId) {
              selectScenario(newId);
              navigate(`/deals/${dealId}/scenarios/${newId}/analysis`);
            }
          } finally {
            setLoading(false);
            setShowCreateModal(false);
          }
        }}
      />

      {/* Scenario Edit Modal */}
      <ScenarioCreateModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        existingScenarios={scenarios}
        editingScenario={currentScenario ? {
          id: currentScenario.id,
          name: currentScenario.name,
          headlinePrice: currentScenario.headline_price,
          businessName: currentScenario.business_name,
          bankAmount: currentScenario.bank_amount,
          bankRateAnnual: currentScenario.bank_rate_annual,
          bankTermYears: currentScenario.bank_term_years,
          sellerAmount: currentScenario.seller_amount,
          sellerRateAnnual: currentScenario.seller_rate_annual,
          sellerStructure: currentScenario.seller_structure as 'amort' | 'io' | 'io_then_amort',
          sellerAmortizationYears: currentScenario.seller_amortization_years,
          sellerBalloonYears: currentScenario.seller_balloon_years,
          sellerBalloonDueMonth: currentScenario.seller_balloon_due_month,
          sellerIoMonths: currentScenario.seller_io_months,
          annualSde: currentScenario.annual_sde,
          monthlySde: currentScenario.monthly_sde,
          financialMetricType: currentScenario.financial_metric_type,
          annualThreshold: currentScenario.annual_threshold,
          quarterlyThreshold: currentScenario.quarterly_threshold
        } : undefined}
        onUpdate={async (scenarioId, scenarioData) => {
          if (!user?.id) return;
          setLoading(true);
          try {
            const res = await DatabaseService.updateDealScenario(scenarioId, {
              name: scenarioData.name,
              metadata: {
                headlinePrice: scenarioData.headlinePrice,
                businessName: scenarioData.businessName,
                bankAmount: scenarioData.bankAmount,
                bankRateAnnual: scenarioData.bankRateAnnual,
                bankTermYears: scenarioData.bankTermYears,
                sellerAmount: scenarioData.sellerAmount,
                sellerRateAnnual: scenarioData.sellerRateAnnual,
                sellerStructure: scenarioData.sellerStructure,
                sellerAmortizationYears: scenarioData.sellerAmortizationYears,
                sellerBalloonYears: scenarioData.sellerBalloonYears,
                sellerBalloonDueMonth: scenarioData.sellerBalloonDueMonth,
                sellerIoMonths: scenarioData.sellerIoMonths,
                annualSde: scenarioData.annualSde,
                monthlySde: scenarioData.monthlySde,
                financialMetricType: scenarioData.financialMetricType,
                annualThreshold: scenarioData.annualThreshold,
                quarterlyThreshold: scenarioData.quarterlyThreshold
              }
            }, user.id);
            if (res.error) {
              setError(res.error);
              return;
            }
            // Refresh scenario data
            const list = await DatabaseService.listDealScenarios(dealId!, user.id);
            if (list.data) {
              const scenario = list.data.find((s: any) => s.id === selectedScenarioId);
              if (scenario) {
                setCurrentScenario(scenario);
              }
            }
          } finally {
            setLoading(false);
            setShowEditModal(false);
          }
        }}
      />

      {/* Scenario Clone Modal */}
      <ScenarioCreateModal
        isOpen={showCloneModal}
        onClose={() => setShowCloneModal(false)}
        existingScenarios={scenarios}
        cloningScenario={currentScenario ? { id: currentScenario.id, name: currentScenario.name } : undefined}
        onClone={async (sourceScenarioId, scenarioData) => {
          if (!user?.id) return;
          setLoading(true);
          try {
            const res = await DatabaseService.cloneDealScenario(sourceScenarioId, scenarioData.name, user.id);
            if (res.error) {
              setError(res.error);
              return;
            }
            const list = await DatabaseService.listDealScenarios(dealId!, user.id);
            const opts = (list.data || []).map((s: any) => ({ id: s.id, name: s.name }));
            setScenarios(opts);
            const newId = res.data?.id || opts[0]?.id || null;
            if (newId) {
              selectScenario(newId);
              navigate(`/deals/${dealId}/scenarios/${newId}/analysis`);
            }
          } finally {
            setLoading(false);
            setShowCloneModal(false);
          }
        }}
      />
    </div>
  );
}
